import { useEffect, useState, useCallback } from 'react';
import { Card } from './ui/card';
import { Clock, Coffee, WifiOff, Briefcase } from 'lucide-react';
import api from '../utils/api';

const CTL_LABEL = {
  meeting: 'Meeting',
  downtime: 'Client Downtime',
  gst_visit: 'GST Visit',
  call_for_data: 'Call for Data',
  transition: 'Transition',
  other: 'Other',
};

/**
 * "My day summary" — today's time breakdown for the current user at a glance.
 * Mounted on MemberWorkspace + Dashboard (admin & super admin views).
 * Self-refresh every 60s and on every ws-notification so values stay live.
 */
export const MyDaySummaryCard = () => {
  const [data, setData] = useState(null);

  const fetchSummary = useCallback(() => {
    api.get('/my-day-summary')
      .then(res => setData(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchSummary();
    const id = setInterval(fetchSummary, 60000);
    const onWs = () => fetchSummary();
    window.addEventListener('ws-notification', onWs);
    return () => { clearInterval(id); window.removeEventListener('ws-notification', onWs); };
  }, [fetchSummary]);

  if (!data) return null;

  const fmtHrs = (h) => {
    if (!h || h <= 0) return '0h';
    const whole = Math.floor(h);
    const min = Math.round((h - whole) * 60);
    if (whole === 0) return `${min}m`;
    if (min === 0) return `${whole}h`;
    return `${whole}h ${min}m`;
  };
  const fmtMin = (m) => {
    if (!m || m <= 0) return '0m';
    const h = Math.floor(m / 60);
    const r = m % 60;
    if (h === 0) return `${r}m`;
    if (r === 0) return `${h}h`;
    return `${h}h ${r}m`;
  };

  const ctlPairs = Object.entries(data.client_time_by_category || {}).filter(([, v]) => v > 0);

  return (
    <Card className="border-slate-200 shadow-sm mb-4 bg-white" data-testid="my-day-summary-card">
      <div className="px-5 py-3.5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <Clock className="h-4 w-4 text-[#5B6FD6]" />
            My day so far
          </h3>
          <span className="text-xs text-slate-400">{data.date}</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Tile icon={<Briefcase className="h-4 w-4" />} label="Tasks" value={fmtHrs(data.task_hours)} color="bg-blue-50 text-blue-700" testid="myday-tasks" />
          <Tile icon={<Coffee className="h-4 w-4" />} label="Lunch" value={fmtMin(data.lunch_minutes)} color="bg-orange-50 text-orange-700" testid="myday-lunch" />
          <Tile icon={<WifiOff className="h-4 w-4" />} label="Network down" value={fmtMin(data.network_downtime_minutes)} color="bg-red-50 text-red-700" testid="myday-network" />
          <Tile icon={<Clock className="h-4 w-4" />} label="Total" value={fmtHrs(data.total_hours)} color="bg-emerald-50 text-emerald-700 font-bold" testid="myday-total" />
        </div>
        {ctlPairs.length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <p className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5">Client time</p>
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              {ctlPairs.map(([k, v]) => (
                <span key={k} className="text-xs text-slate-600" data-testid={`myday-ctl-${k}`}>
                  <span className="font-semibold text-slate-800">{fmtHrs(v)}</span>{' '}
                  <span className="text-slate-500">{CTL_LABEL[k] || k}</span>
                </span>
              ))}
              <span className="text-xs text-slate-400">· total {fmtHrs(data.client_time_total)}</span>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

const Tile = ({ icon, label, value, color, testid }) => (
  <div className={`rounded-md px-3 py-2 ${color}`} data-testid={testid}>
    <div className="flex items-center gap-1.5 mb-0.5 opacity-80">
      {icon}
      <span className="text-[11px] uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-base font-mono font-semibold">{value}</p>
  </div>
);
